import { useCallback, useEffect, useMemo, useState } from "react";
import { staffDocsApi } from "@/api";
import { Button, Pill, Spinner, Toast, useToast } from "@/components/ui";
import { formatDateShort } from "@/lib/format";
import { formatBytes, shareStatusToPill } from "@/lib/shares";
import type { DocumentShare } from "@/types";
import { useChatFeed } from "./ChatContext";
import { ChatSidebar } from "./ChatSidebar";
import { ShareDrawer } from "./ShareDrawer";
import "./chat.css";

/** Chat › Documents (§4.4): the staff view over the share lifecycle. Share list
 *  from S5 (all statuses + latest decision), actions by status (draft → Send /
 *  Delete; sent|viewed → Withdraw; live file → View via the staff download-url;
 *  decided → Supersede), and the upload → share → send flow in a drawer.
 *  Status chips go through shareStatusToPill — raw statuses never render. */
export function DocumentsPage() {
  const { jobs, selectedJobId, setSelectedJobId } = useChatFeed();
  const selected = useMemo(
    () => jobs?.find((j) => j.job_id === selectedJobId) ?? null,
    [jobs, selectedJobId],
  );

  const [shares, setShares] = useState<DocumentShare[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyShareId, setBusyShareId] = useState<number | null>(null);
  const [drawer, setDrawer] = useState<{ supersedes: DocumentShare | null } | null>(null);
  const { toast, showToast } = useToast();

  const load = useCallback(async (jobId: number, signal?: AbortSignal) => {
    try {
      setShares(await staffDocsApi.listShares(jobId, signal));
      setLoadError(null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setLoadError(err instanceof Error ? err.message : "Could not load the documents.");
    }
  }, []);

  useEffect(() => {
    if (!selectedJobId) {
      setShares(null);
      return;
    }
    setShares(null);
    setLoadError(null);
    const ctrl = new AbortController();
    void load(selectedJobId, ctrl.signal);
    return () => ctrl.abort();
  }, [selectedJobId, load]);

  async function withBusy(share: DocumentShare, fn: () => Promise<unknown>, doneMsg: string) {
    setBusyShareId(share.share_id);
    try {
      await fn();
      if (selectedJobId) await load(selectedJobId);
      showToast(doneMsg);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "The action failed.", "warn");
    } finally {
      setBusyShareId(null);
    }
  }

  function doSend(share: DocumentShare) {
    if (!window.confirm(`Send "${share.title}" to the client?`)) return;
    void withBusy(share, () => staffDocsApi.sendShare(share.share_id), "Sent to client");
  }

  function doWithdraw(share: DocumentShare) {
    if (!window.confirm(`Withdraw "${share.title}"? The client will no longer see it.`)) return;
    void withBusy(share, () => staffDocsApi.withdrawShare(share.share_id), "Withdrawn");
  }

  function doDelete(share: DocumentShare) {
    if (!window.confirm(`Delete the draft "${share.title}"? This also removes the uploaded file.`))
      return;
    void withBusy(
      share,
      async () => {
        await staffDocsApi.deleteShare(share.share_id);
        // Drafts own their upload — tidy the file too (best-effort).
        await staffDocsApi.deleteDrawing(share.drawing_id).catch(() => undefined);
      },
      "Draft deleted",
    );
  }

  async function doView(share: DocumentShare) {
    try {
      const { url } = await staffDocsApi.getDownloadUrl(share.drawing_id);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not open the file.", "warn");
    }
  }

  function onDrawerDone(result: "draft" | "sent" | "send_failed") {
    setDrawer(null);
    if (selectedJobId) void load(selectedJobId);
    if (result === "sent") showToast("Uploaded and sent to the client");
    else if (result === "draft") showToast("Uploaded as a draft");
    else showToast("Uploaded as a draft, but sending failed — use Send on the card.", "warn");
  }

  return (
    <div className="chat-split">
      <ChatSidebar jobs={jobs} selectedJobId={selectedJobId} onSelect={setSelectedJobId} />

      <section className="chat-main" aria-label="Documents">
        {!selected ? (
          <p className="chat-main__empty">Select a job to manage its shared documents.</p>
        ) : (
          <>
            <header className="chat-main__head">
              <div>
                <h2 className="chat-main__title">
                  {selected.job_number > 0 ? `${selected.job_number} · ` : ""}
                  {selected.name}
                </h2>
                <span className="chat-main__subtitle">{selected.client_names}</span>
              </div>
              <Button onClick={() => setDrawer({ supersedes: null })}>Upload &amp; share</Button>
            </header>

            <div className="doc-list">
              {loadError ? (
                <div className="chat-thread__error" role="alert">
                  <p>{loadError}</p>
                  <Button variant="secondary" size="sm" onClick={() => void load(selected.job_id)}>
                    Try again
                  </Button>
                </div>
              ) : shares === null ? (
                <div className="chat-thread__loading">
                  <Spinner />
                </div>
              ) : shares.length === 0 ? (
                <p className="chat-thread__empty">
                  Nothing shared yet — upload a document to send it for review or for
                  information.
                </p>
              ) : (
                shares.map((s) => (
                  <ShareCard
                    key={s.share_id}
                    share={s}
                    busy={busyShareId === s.share_id}
                    onSend={() => doSend(s)}
                    onWithdraw={() => doWithdraw(s)}
                    onDelete={() => doDelete(s)}
                    onView={() => void doView(s)}
                    onSupersede={() => setDrawer({ supersedes: s })}
                  />
                ))
              )}
            </div>
          </>
        )}
      </section>

      {drawer && selectedJobId ? (
        <ShareDrawer
          jobId={selectedJobId}
          supersedes={drawer.supersedes}
          onClose={() => setDrawer(null)}
          onDone={onDrawerDone}
        />
      ) : null}
      <Toast toast={toast} />
    </div>
  );
}

function ShareCard({
  share,
  busy,
  onSend,
  onWithdraw,
  onDelete,
  onView,
  onSupersede,
}: {
  share: DocumentShare;
  busy: boolean;
  onSend: () => void;
  onWithdraw: () => void;
  onDelete: () => void;
  onView: () => void;
  onSupersede: () => void;
}) {
  const pill = shareStatusToPill(share.status);
  const live = share.status === "sent" || share.status === "viewed";
  // Info shares carry no due date (S1 zeroes it) and are never overdue.
  const overdue =
    share.requires_approval && live && share.due_by > 0 && share.due_by < Date.now();
  const canSupersede = live || share.status === "approved" || share.status === "changes_requested";
  const decision = share.latest_decision;

  return (
    <article className="doc-card">
      <header className="doc-card__head">
        <h3 className="doc-card__title">{share.title || share.file_name}</h3>
        <span className="doc-card__pills">
          {!share.requires_approval ? <Pill tone="neutral">For information</Pill> : null}
          <Pill tone={pill.tone}>{pill.label}</Pill>
          {overdue ? <Pill tone="danger">Overdue</Pill> : null}
        </span>
      </header>

      <p className="doc-card__facts">
        {share.file_name}
        {share.page_count > 0 ? ` · ${share.page_count} pp` : ""}
        {` · ${formatBytes(share.file_size)}`}
      </p>

      {share.message ? <p className="doc-card__message">“{share.message}”</p> : null}

      <p className="doc-card__meta">
        {share.created_by ? `Created by ${share.created_by} · ` : ""}
        {formatDateShort(share.created_at)}
        {share.sent_at > 0 ? ` · Sent ${formatDateShort(share.sent_at)}` : ""}
        {share.viewed_at > 0 ? ` · Viewed ${formatDateShort(share.viewed_at)}` : ""}
        {share.due_by > 0 ? ` · Due ${formatDateShort(share.due_by)}` : ""}
      </p>

      {decision ? (
        <div className="doc-card__decision">
          <p className="doc-card__decision-head">
            {decision.decision === "approved" ? "Approved" : "Changes requested"} by{" "}
            {decision.decided_by_name} · {formatDateShort(decision.decided_at)}
          </p>
          {decision.comment ? <p className="doc-card__decision-comment">“{decision.comment}”</p> : null}
        </div>
      ) : null}

      <div className="doc-card__actions">
        <Button variant="secondary" size="sm" onClick={onView} disabled={busy}>
          View
        </Button>
        {share.status === "draft" ? (
          <>
            <Button size="sm" onClick={onSend} loading={busy}>
              Send
            </Button>
            <Button variant="danger" size="sm" onClick={onDelete} disabled={busy}>
              Delete
            </Button>
          </>
        ) : null}
        {live ? (
          <Button variant="secondary" size="sm" onClick={onWithdraw} loading={busy}>
            Withdraw
          </Button>
        ) : null}
        {canSupersede ? (
          <Button variant="ghost" size="sm" onClick={onSupersede} disabled={busy}>
            Supersede
          </Button>
        ) : null}
      </div>
    </article>
  );
}